import {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query'

import Toast from 'components/Basic/Toast'
import {AIUsageApp} from 'interfaces'
import {AIUsageService} from 'services/api'

interface AIUsageAppsQueryResponseType {
  fetchNextPage: () => Promise<UseInfiniteQueryResult>
  hasNextPage: boolean
  apps: AIUsageApp[]
  isLoading: boolean
  isFetchingNextPage: boolean
  isSuccess: boolean
  isError: boolean
  error: unknown
}

interface UseAIUsageAppsQueryOptions {
  enabled?: boolean
}

export const AI_USAGE_APPS_QUERY_KEY = 'getAIUsageApps'

export const useAIUsageAppsQuery = ({
  enabled = true,
}: UseAIUsageAppsQueryOptions = {}): AIUsageAppsQueryResponseType => {
  const {t} = useTranslation(['aiUsage'])

  const {
    data,
    fetchNextPage,
    isLoading,
    isSuccess,
    isError,
    error,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [AI_USAGE_APPS_QUERY_KEY],
    queryFn: ({pageParam}) => {
      return AIUsageService.getAIUsageApps({
        after: pageParam,
      })
    },
    getNextPageParam: lastPage => {
      const pageInfo = lastPage?.data?.pageInfo
      return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined
    },
    enabled,
  })

  const hasNextPage = useMemo(() => {
    const lastPage = data?.pages?.at(-1)
    return Boolean(lastPage?.data?.pageInfo?.hasNextPage)
  }, [data?.pages])

  useEffect(() => {
    if (isError) {
      Toast({
        message: t('aiUsage:AI_USAGE_TOAST_APPS_FETCH_ERROR'),
        type: 'error',
        customId: 'ai-usage-apps-fetch-error',
      })
    }
  }, [isError, t])

  const apps = useMemo(() => {
    return data?.pages?.flatMap(page => page?.data?.apps) ?? []
  }, [data?.pages])

  return {
    fetchNextPage,
    hasNextPage,
    apps,
    isLoading,
    isFetchingNextPage,
    isSuccess,
    isError,
    error,
  }
}
